import { useRoute } from 'vue-router';
import useCountry from '@/composables/countryDependable';
import completeCart from '@/core/api/cart';
import { useApplicationStore } from '@/store/application';
import { useTrademarkStore } from '@/store/mark';
import { useCartReferenceStore } from '@/store/cart-reference';
import { resetAllStores, useImportData } from '@/store/store-utils';
import { MarkOwnerType } from '@/core/interface/api-interface';
import useLoggable from './loggable';
import { CountryCode } from '@/config/country/type';

export const useLoadCart = async (): Promise<boolean> => {
    const route = useRoute();
    const country = useCountry();
    const { info } = useLoggable();
    const countryCode = country.displayCode.toLowerCase();
    const cartRef = route.params.cartRef?.toString();
    const applicationStore = useApplicationStore();
    const trademarkStore = useTrademarkStore();
    const cartReferenceStore = useCartReferenceStore();
    const { importDataAllStores } = useImportData();

    try {
        const response = await completeCart.load(countryCode, cartRef);
        const { mark, applicationRef } = response;

        // Patch application and cart reference stores
        applicationStore.appReference = applicationRef;
        cartReferenceStore.cartReference = cartRef;

        if (!mark) {
            // TODO: ADD LOGS
            return false;
        }

        // Patch trademark store (if needed)
        trademarkStore.importData({
            trademarkWord: mark.markWord?.word || null,
            trademarkImage: mark.markImage ? { url: `/content/image/${mark.markImage?.originalFilePath}`, ref: mark.markImage?.imageRef } : null,
            uploadedAsWordImage: mark.markImage?.uploadedAsWordImage || false,
        });

        let ownerType =  MarkOwnerType.PERSON
        const ownerNames = mark.markOwners.map((owner) => {
            if (owner.markOwnerType) {
                if (owner.markOwnerType.name === 'Person') {
                    ownerType = MarkOwnerType.PERSON;
                    let name = owner.firstName || '';
                    if (owner.lastName) {
                        name += ' ' + owner.lastName;
                    }
                    return name;
                } else {
                    ownerType = MarkOwnerType.BUSINESS;
                    return owner.businessName || '';
                }
            }

            return '';
        });

        const businessNumber = mark.markOwners[0]?.businessNumber || mark.markOwners[0]?.companyNumber || '';
        if (mark.markOwnerDetail) {
            importDataAllStores({
                owner: {
                    address: notNullString(mark.markOwnerDetail.address),
                    suburb: notNullString(mark.markOwnerDetail.suburb),
                    postCode: notNullString(mark.markOwnerDetail.postCode),
                    stateRegion: country.code !== CountryCode.NZ ? notNullString(mark.markOwnerDetail.region) : notNullString(mark.markOwnerDetail.city),
                    country: notNullString(mark.markOwnerDetail.countryCode),
                    email: mark.markOwnerDetail.email,
                    mobileNumber: mark.markOwnerDetail.phone,
                    ownerType,
                    ownerNames,
                    businessNumber,
                }
            })
        }

        if (mark.markContact) {
            let contactName = ownerNames.length > 0 ? ownerNames[0] : '';

            if (mark.markContact.firstName) {
                contactName = mark.markContact.firstName;
                if (mark.markContact.lastName) {
                    contactName += ' ' + mark.markContact.lastName;
                }
            }

            importDataAllStores({
                contact: {
                    contactEmail: mark.markContact.email,
                    contactName,
                }
            })
        }

        importDataAllStores({
            product: { goods: mark.markGoods.map(good => {
                return {
                    externalId: good.externalId,
                    classNumber: Number(good.classNumber),
                    name: good.name,
                };
            })},
            termOfUse: {
                //The data for load cart is created when user navigate to the payment page, isConfirm was set to true but not save in the backend
                isConfirm: true, 
            }
        });

        return true;
    } catch (e) {
        info('Error loading cart', {error: e});
        resetAllStores();
        return false;
    }
};

const notNullString = (property: string|null) => {
    if (property) {
        return property;
    }

    return '';
}

